import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { DisplaySizes } from '@newcore/ui/enums/typography-display-sizes';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Display, Text } from '@newcore/ui/typography';
import * as Sentry from '@sentry/react';
import { errorTracker } from '@/common/error-tracking';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to error tracking service
    if (errorInfo.componentStack) {
      errorTracker.captureReactError(error, {
        componentStack: errorInfo.componentStack,
      });
    }

    Sentry.captureReactException(error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className='min-h-screen flex items-center justify-center bg-primary p-4'>
          <div className='max-w-md w-full space-y-4'>
            <div className='text-center space-y-2'>
              <Display as='h1' size={DisplaySizes.XS} weight={FontWeights.BOLD}>
                Something went wrong
              </Display>
              <Text as='p' className='text-secondary'>
                We encountered an unexpected error. Please try refreshing the
                page.
              </Text>
            </div>

            {import.meta.env.DEV && this.state.error && (
              <div className='mt-4 p-4 bg-secondary rounded-lg'>
                <Text as='p' className='font-mono text-error-primary mb-2'>
                  {this.state.error.toString()}
                </Text>
                {this.state.errorInfo && (
                  <details className='text-secondary'>
                    <summary className='cursor-pointer hover:text-primary'>
                      View stack trace
                    </summary>
                    <Text
                      as='pre'
                      size={TextSizes.XS}
                      className='mt-2 whitespace-pre-wrap'
                    >
                      {this.state.errorInfo.componentStack}
                    </Text>
                  </details>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
